import * as React from "react";
import {
  ArrowLeft, Search, Loader2, ChevronRight, DollarSign, TrendingUp,
  AlertTriangle, BarChart3, HelpCircle, ArrowUpDown, PieChart
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const HelpTip = ({ text, side = "top" }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild><HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" /></TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-slate-900 text-slate-100 px-3 py-2">{text}</TooltipContent>
  </Tooltip>
);
const fmt = (n) => n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "0";
const fmtM = (n) => { if (!n) return "$0"; if (n >= 1000000) return "$" + (n/1000000).toFixed(1) + "M"; if (n >= 1000) return "$" + (n/1000).toFixed(1) + "K"; return "$" + n.toFixed(0); };
const PLAT_C = { google_ads: "bg-blue-100 text-blue-700", facebook_ads: "bg-indigo-100 text-indigo-700", tiktok_ads: "bg-slate-800 text-white", dv360: "bg-green-100 text-green-700" };

// === CLIENT LIST ===
const ClientList = ({ onSelect, days, setDays }) => {
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/budget-utilization/clients?days=${days}`, { withCredentials: true });
        setClients(res.data.clients || []);
        trackActivity("view_module", "budget_utilization");
      } catch (e) { toast.error("Error al cargar clientes"); }
      finally { setIsLoading(false); }
    })();
  }, [days]);

  const filtered = clients.filter(c => (c.cliente || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Budget Utilization</h2>
          <p className="text-slate-500 text-sm mt-1">{clients.length} clientes activos</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>
      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Card key={c.cliente} className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${c.pct_spend_low_roas > 50 ? "border-red-200" : c.pct_spend_low_roas > 30 ? "border-amber-200" : ""}`}
              onClick={() => { trackActivity("select_client", "budget_utilization", { cliente: c.cliente }); onSelect(c.cliente); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-800">{c.cliente}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.campaigns} campanas · {fmtM(c.total_spend)}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
                {/* Efficiency bar */}
                <div className="w-full h-2 rounded-full overflow-hidden flex bg-slate-100 mb-2">
                  {c.high_efficiency > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${c.high_efficiency/c.campaigns*100}%` }} />}
                  {c.mid_efficiency > 0 && <div className="bg-amber-400 h-full" style={{ width: `${c.mid_efficiency/c.campaigns*100}%` }} />}
                  {c.low_efficiency > 0 && <div className="bg-red-400 h-full" style={{ width: `${c.low_efficiency/c.campaigns*100}%` }} />}
                </div>
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>{c.high_efficiency} eficientes</span>
                  <span>{c.low_efficiency} bajo rendimiento</span>
                </div>
                {c.pct_spend_low_roas > 30 && (
                  <div className="mt-2 text-[10px] text-red-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{c.pct_spend_low_roas}% del gasto en ROAS &lt; 1
                  </div>
                )}
                {c.unclassified_pct >= 30 && (
                  <div className="mt-1 text-[10px] text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />{c.unclassified_camps} sin clasificar ({c.unclassified_pct}%) — afecta metricas
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// === DASHBOARD ===
const Dashboard = ({ cliente, days, setDays, onBack }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [sortKey, setSortKey] = React.useState("spend");
  const [sortDir, setSortDir] = React.useState("desc");

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/budget-utilization/dashboard/${encodeURIComponent(cliente)}?days=${days}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { toast.error("Error al cargar"); }
      finally { setIsLoading(false); }
    })();
  }, [cliente, days]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { summary: s, by_platform, campaigns } = data;

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sorted = [...campaigns].sort((a, b) => sortDir === "desc" ? (b[sortKey]||0) - (a[sortKey]||0) : (a[sortKey]||0) - (b[sortKey]||0));
  const SortTH = ({ k, children, className = "" }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 ${className}`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-0.5">{children}{sortKey === k && <ArrowUpDown className="h-3 w-3 text-indigo-500" />}</span>
    </TableHead>
  );

  const roasColor = (r) => r >= 3 ? "text-emerald-600" : r >= 1 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Clientes</Button>
          <div className="h-5 w-px bg-slate-200" />
          <h2 className="text-xl font-bold text-slate-800">{cliente}</h2>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem><SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem><SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Inversion Total", value: fmtM(s.total_spend), color: "text-slate-800", tip: "Gasto total en el periodo seleccionado" },
          { label: "Concentracion Top 20%", value: `${s.top_20_share}%`, color: s.top_20_share > 80 ? "text-red-600" : "text-slate-800", tip: "Que % del gasto total consumen el 20% de campanas con mas gasto. >80% indica alta concentracion" },
          { label: "Gasto Ineficiente", value: fmtM(s.wasted_spend), color: s.wasted_pct > 30 ? "text-red-600" : "text-amber-600", tip: `${s.wasted_pct}% del gasto total en campanas con ROAS < 1 (no recuperan la inversion)` },
          { label: "ROAS >= 3", value: `${s.high_efficiency}`, color: "text-emerald-600", tip: "Campanas con retorno 3x o mayor sobre la inversion" },
          { label: "Sin Conversiones", value: `${s.zero_conversions}`, color: s.zero_conversions > s.total_campaigns * 0.5 ? "text-red-600" : "text-slate-600", tip: "Campanas que gastan presupuesto pero no generan conversiones" },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <p className="text-[11px] text-slate-400 inline-flex items-center">{kpi.label}<HelpTip text={kpi.tip} /></p>
              <p className={`text-lg font-bold mt-0.5 ${kpi.color}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Efficiency Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Distribucion de Eficiencia
            <HelpTip text="Clasifica campanas por ROAS. Verde: ROAS>=3 (alta eficiencia). Amarillo: ROAS 1-3 (media). Rojo: ROAS<1 (bajo). Gris: sin conversiones." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Alta (ROAS >= 3)", count: s.high_efficiency, spend: campaigns.filter(c=>c.roas>=3).reduce((a,c)=>a+c.spend,0), color: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" },
              { label: "Media (ROAS 1-3)", count: s.mid_efficiency, spend: campaigns.filter(c=>c.roas>=1&&c.roas<3).reduce((a,c)=>a+c.spend,0), color: "bg-amber-50 border-amber-200", text: "text-amber-700" },
              { label: "Baja (ROAS < 1)", count: s.low_efficiency, spend: campaigns.filter(c=>c.roas>0&&c.roas<1).reduce((a,c)=>a+c.spend,0), color: "bg-red-50 border-red-200", text: "text-red-700" },
              { label: "Sin Conversiones", count: s.zero_conversions, spend: campaigns.filter(c=>c.roas===0).reduce((a,c)=>a+c.spend,0), color: "bg-slate-50 border-slate-200", text: "text-slate-600" },
            ].map((b, i) => (
              <div key={i} className={`p-3 rounded-lg border ${b.color}`}>
                <p className={`text-xs font-medium ${b.text}`}>{b.label}</p>
                <p className={`text-xl font-bold ${b.text}`}>{b.count}</p>
                <p className="text-[10px] text-slate-400 mt-1">{fmtM(b.spend)} invertidos</p>
              </div>
            ))}
          </div>
          {/* Visual bar */}
          <div className="mt-3 w-full h-3 rounded-full overflow-hidden flex bg-slate-100">
            {s.high_efficiency > 0 && <div className="bg-emerald-500 h-full transition-all" style={{ width: `${s.high_efficiency/s.total_campaigns*100}%` }} />}
            {s.mid_efficiency > 0 && <div className="bg-amber-400 h-full transition-all" style={{ width: `${s.mid_efficiency/s.total_campaigns*100}%` }} />}
            {s.low_efficiency > 0 && <div className="bg-red-400 h-full transition-all" style={{ width: `${s.low_efficiency/s.total_campaigns*100}%` }} />}
            {s.zero_conversions > 0 && <div className="bg-slate-300 h-full transition-all" style={{ width: `${s.zero_conversions/s.total_campaigns*100}%` }} />}
          </div>
        </CardContent>
      </Card>

      {/* By Platform */}
      {by_platform.length > 1 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribucion por Plataforma</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {by_platform.map(p => (
                <div key={p.platform} className="p-3 rounded-lg border bg-slate-50">
                  <Badge className={`text-[10px] mb-2 ${PLAT_C[p.platform] || "bg-slate-100"}`}>{p.platform}</Badge>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><p className="text-slate-400">Spend</p><p className="font-bold">{fmtM(p.spend)}</p></div>
                    <div><p className="text-slate-400">% Total</p><p className="font-bold">{p.spend_share}%</p></div>
                    <div><p className="text-slate-400">ROAS</p><p className={`font-bold ${roasColor(p.roas)}`}>{p.roas}x</p></div>
                    <div><p className="text-slate-400">Campanas</p><p className="font-bold">{p.campaigns}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Campaigns Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Campanas ({campaigns.length})
            <HelpTip text="Ordenadas por gasto. La columna Acumulado muestra el % acumulado del gasto total — identifica cuantas campanas concentran la mayor parte del presupuesto." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[500px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[4%] sticky top-0 bg-white">#</TableHead>
                  <TableHead className="w-[24%] sticky top-0 bg-white">Campana</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Obj.</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Plataf.</TableHead>
                  <SortTH k="spend" className="text-right w-[9%] sticky top-0 bg-white">Spend</SortTH>
                  <SortTH k="pct_of_total" className="text-right w-[7%] sticky top-0 bg-white">% Total</SortTH>
                  <TableHead className="text-right w-[8%] sticky top-0 bg-white">Acumulado</TableHead>
                  <TableHead className="text-right w-[8%] sticky top-0 bg-white">KPI Ppal.</TableHead>
                  <SortTH k="conversions" className="text-right w-[7%] sticky top-0 bg-white">Conv.</SortTH>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Eficiencia</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((c, i) => {
                  const OBJ_S = { awareness: {c:"bg-purple-100 text-purple-700",l:"Awareness"}, consideration: {c:"bg-blue-100 text-blue-700",l:"Consideration"}, conversion: {c:"bg-emerald-100 text-emerald-700",l:"Conversion"}, unclassified: {c:"bg-slate-100 text-slate-500",l:"N/C"} };
                  const objS = OBJ_S[c.objective] || OBJ_S.unclassified;
                  // Smart efficiency: awareness=CPM, consideration=CPC, conversion=ROAS
                  let effLabel, effColor, kpiLabel;
                  if (c.objective === 'awareness') {
                    kpiLabel = `CPM $${fmt(c.cpm)}`;
                    effLabel = c.cpm > 0 ? "OK" : "N/A";
                    effColor = "bg-purple-50 text-purple-600";
                  } else if (c.objective === 'consideration') {
                    kpiLabel = `CPC $${fmt(c.cpc)}`;
                    effLabel = c.ctr > 1 ? "OK" : "Bajo CTR";
                    effColor = c.ctr > 1 ? "bg-blue-50 text-blue-600" : "bg-amber-100 text-amber-700";
                  } else {
                    kpiLabel = `ROAS ${c.roas}x`;
                    effLabel = c.roas >= 3 ? "Alta" : c.roas >= 1 ? "Media" : c.conversions > 0 ? "Baja" : "Sin conv.";
                    effColor = c.roas >= 3 ? "bg-emerald-100 text-emerald-700" : c.roas >= 1 ? "bg-amber-100 text-amber-700" : c.conversions > 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500";
                  }
                  return (
                    <TableRow key={c.campaign_id} className="text-xs">
                      <TableCell className="text-slate-400 font-mono">{c.rank_num}</TableCell>
                      <TableCell><p className="font-medium text-slate-700 truncate max-w-[200px]">{c.campaign_name}</p></TableCell>
                      <TableCell><Badge className={`text-[9px] ${objS.c}`}>{objS.l}</Badge></TableCell>
                      <TableCell><Badge className={`text-[9px] ${PLAT_C[c.platform] || "bg-slate-100"}`}>{c.platform?.replace("_ads","")}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{fmtM(c.spend)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-500">{c.pct_of_total}%</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="bg-indigo-400 h-full rounded-full" style={{ width: `${Math.min(c.cumulative_pct, 100)}%` }} />
                          </div>
                          <span className="font-mono text-slate-500">{c.cumulative_pct}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-700">{kpiLabel}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(c.conversions)}</TableCell>
                      <TableCell><Badge className={`text-[9px] ${effColor}`}>{effLabel}</Badge></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// === MAIN ===
export const BudgetUtilizationView = ({ onBack }) => {
  const [view, setView] = React.useState("clients");
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [days, setDays] = React.useState(30);
  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Volver</Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                <PieChart className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                  Budget Utilization
                  <HelpTip text="Analiza como se distribuye el presupuesto entre campanas, identifica gasto ineficiente y concentracion excesiva." side="bottom" />
                </h1>
                <p className="text-xs text-slate-500">Concentracion, eficiencia y oportunidades de optimizacion</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {view === "clients" && <ClientList onSelect={(c) => { setSelectedClient(c); setView("dashboard"); }} days={days} setDays={setDays} />}
        {view === "dashboard" && <Dashboard cliente={selectedClient} days={days} setDays={setDays} onBack={() => { setSelectedClient(null); setView("clients"); }} />}
      </main>
    </div>
    </TooltipProvider>
  );
};
